import React, { useEffect, useState, Children, isValidElement, useCallback } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Header } from '../components/Header'
import { Footer } from '../components/Footer'
import { CTASection } from '../components/CTASection'
import { getBlogPost, type BlogPost as BlogPostType } from '../services/outline'
import { motion } from 'framer-motion'
import { ArrowLeftIcon, CalendarIcon, ClockIcon, CopyIcon, CheckIcon } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import rehypeHighlight from 'rehype-highlight'
import { Mermaid } from '../components/Mermaid'
import { slugify } from '../utils/slugify'
import { SeoHelmet } from '../components/SeoHelmet'

function CopyButton({ code }: { code: string }) {
  const [copied, setCopied] = useState(false)
  const copyCode = useCallback(() => {
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [code])
  return (
    <button
      onClick={copyCode}
      className="absolute top-2 right-2 px-2.5 py-1.5 text-xs font-medium rounded-lg
        bg-gray-700 text-gray-300 opacity-0 group-hover:opacity-100
        hover:bg-gray-600 hover:text-white transition-all"
    >
      {copied ? (
        <span className="flex items-center gap-1">
          <CheckIcon className="w-3.5 h-3.5" />
          Copied
        </span>
      ) : (
        <span className="flex items-center gap-1">
          <CopyIcon className="w-3.5 h-3.5" />
          Copy
        </span>
      )}
    </button>
  )
}

export function BlogPost() {
  const { id, slug } = useParams<{ id: string; slug: string }>()
  const navigate = useNavigate()
  const [post, setPost] = useState<BlogPostType | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function loadPost() {
      if (!id) return
      try {
        const data = await getBlogPost(id)
        if (data) {
          const expectedSlug = slugify(data.title)
          if (!slug || slug !== expectedSlug) {
            navigate(`/blog/${id}/${expectedSlug}`, { replace: true })
            return
          }
          setPost(data)
        } else {
          navigate('/blog')
        }
      } catch {
        setError('Unable to load blog post. Please try again later.')
      } finally {
        setLoading(false)
      }
    }
    loadPost()
  }, [id, slug, navigate])

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-white flex flex-col font-['Roboto']">
        <Header />
        <main className="flex-1 flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </main>
        <Footer />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen w-full bg-white flex flex-col font-['Roboto']">
        <Header />
        <main className="flex-1 flex justify-center items-center">
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl text-center max-w-md">
            {error}
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  if (!post) return null

  const truncatedTitle =
    post.title.length > 60
      ? post.title.slice(0, 57).trimEnd() + '...'
      : post.title
  const postSlug = slugify(post.title)
  const blogUrl = `/blog/${post.id}/${postSlug}`

  const date = new Date(post.publishedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  return (
    <div className="min-h-screen w-full bg-white flex flex-col font-['Roboto']">
      <SeoHelmet
        title={truncatedTitle}
        description={post.excerpt}
        image={`/api/og/image/${post.id}`}
        url={blogUrl}
        pageType="article"
      />
      <Header />

      <main className="flex-1">
        <article className="w-full py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-3xl mx-auto">
            <Link
              to="/blog"
              className="inline-flex items-center text-gray-500 hover:text-primary transition-colors mb-12"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to all insights
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
                <div className="flex items-center gap-1">
                  <CalendarIcon className="w-4 h-4" />
                  <span>{date}</span>
                </div>
                <div className="flex items-center gap-1">
                  <ClockIcon className="w-4 h-4" />
                  <span>{post.readTime}</span>
                </div>
              </div>

              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-12 font-['Poppins'] leading-tight">
                {post.title}
              </h1>

              <div
                className="prose prose-lg prose-blue max-w-none text-gray-700
                prose-headings:font-['Poppins'] prose-headings:font-bold prose-headings:text-gray-900
                prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl
                prose-p:leading-relaxed prose-p:mb-6
                prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                prose-li:my-2
                prose-strong:text-gray-900
                prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-600 prose-blockquote:bg-gray-50 prose-blockquote:py-2 prose-blockquote:pr-4 prose-blockquote:rounded-r-lg
                prose-code:text-primary prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:text-sm
                prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:p-4 prose-pre:rounded-xl prose-pre:overflow-x-auto prose-pre:text-sm
                prose-table:w-full prose-table:border-collapse prose-table:mb-6
                prose-th:border-b-2 prose-th:border-gray-200 prose-th:py-3 prose-th:text-left prose-th:font-bold prose-th:text-gray-900
                prose-td:border-b prose-td:border-gray-100 prose-td:py-3 prose-td:text-gray-700"
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw, rehypeHighlight]}
                  components={{
                    pre({ children, ...props }: any) {
                      const codeElement = Children.toArray(children)[0] as React.ReactElement<{ className?: string; children?: React.ReactNode }>
                      if (
                        isValidElement(codeElement) &&
                        codeElement.props.className?.includes('language-mermaid')
                      ) {
                        return (
                          <Mermaid
                            chart={String(codeElement.props.children).replace(/\n$/, '')}
                          />
                        )
                      }
                      const code = String(codeElement?.props?.children || '').replace(/\n$/, '')
                      return (
                        <div className="relative group">
                          <pre {...props}>{children}</pre>
                          <CopyButton code={code} />
                        </div>
                      )
                    },
                    code({ node, inline, className, children, ...props }: any) {
                      return !inline ? (
                        <code {...props} className={className}>
                          {children}
                        </code>
                      ) : (
                        <code {...props} className={className}>
                          {children}
                        </code>
                      )
                    },
                  }}
                >
                  {post.text}
                </ReactMarkdown>
              </div>
            </motion.div>
          </div>
        </article>

        <CTASection />
      </main>

      <Footer />
    </div>
  )
}